import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  calculateRatioProportion,
  MAX_RATIO_INPUT_LENGTH,
  parseDecimal,
  RESULT_DECIMAL_PLACES,
  type RatioCalculationError,
  type RatioCalculationState,
  type RatioFieldKey,
  type RatioInputs,
  type RatioMode,
} from '@/pages/ratio-proportion-calculator/ratio-proportion-calculator'
import { ratioProportionCalculatorRoute } from '@/shared/utility-registry'

export { calculateRatioProportion } from '@/pages/ratio-proportion-calculator/ratio-proportion-calculator'
export type { RatioMode } from '@/pages/ratio-proportion-calculator/ratio-proportion-calculator'

const UTILITY = ratioProportionCalculatorRoute.analyticsKey

interface TimedCalculation {
  state: RatioCalculationState
  durationMs: number
}

interface ModeOption {
  key: RatioMode
  label: string
  description: string
}

const modes: ModeOption[] = [
  { key: 'solve', label: 'Решить пропорцию', description: 'A:B = C:X' },
  { key: 'simplify', label: 'Сократить отношение', description: 'Например, 1,5:2,25 → 2:3' },
  { key: 'scale', label: 'Масштабировать отношение', description: 'Умножить оба члена на одно число' },
]

const initialInputs: RatioInputs = { a: '', b: '', c: '', multiplier: '' }

export function RatioProportionCalculatorPage() {
  const [mode, setMode] = useState<RatioMode>('solve')
  const [inputs, setInputs] = useState<RatioInputs>(initialInputs)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')
  const lastAnalyticsSnapshot = useRef<string | null>(null)

  const activeMode = useMemo(() => modes.find((item) => item.key === mode) ?? modes[0]!, [mode])
  const calculationRun = useMemo<TimedCalculation>(() => {
    const startMs = performance.now()
    const state = calculateRatioProportion(mode, inputs)
    return { state, durationMs: performance.now() - startMs }
  }, [inputs, mode])
  const calculation = calculationRun.state

  useEffect(() => {
    if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return

    const timeout = window.setTimeout(() => {
      if (!calculation.snapshot || calculation.snapshot === lastAnalyticsSnapshot.current) return
      lastAnalyticsSnapshot.current = calculation.snapshot

      toolInvoke(UTILITY, { mode })
      if (calculation.status === 'ready') {
        toolSuccess(UTILITY, calculationRun.durationMs, { mode })
      } else if (calculation.error) {
        toolError(UTILITY, calculation.error, { mode })
      }
    }, 500)

    return () => window.clearTimeout(timeout)
  }, [calculation.error, calculation.snapshot, calculation.status, calculationRun.durationMs, mode])

  const updateInput = useCallback((field: RatioFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
    setInputs((current) => ({ ...current, [field]: event.target.value }))
    setCopyStatus('idle')
  }, [])

  const reset = useCallback(() => {
    setInputs(initialInputs)
    setCopyStatus('idle')
    lastAnalyticsSnapshot.current = null
  }, [])

  const copyResult = useCallback(async () => {
    if (!calculation.result || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }

    try {
      await navigator.clipboard.writeText(calculation.result.display)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation.result])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-teal-600 via-amber-400 to-red-500" />
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.86fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <label htmlFor="ratio-mode" className="block">
              <span className="mb-1 block text-sm font-medium">Режим</span>
              <select
                id="ratio-mode"
                value={mode}
                onChange={(event) => {
                  setMode(event.target.value as RatioMode)
                  setCopyStatus('idle')
                }}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
              >
                {modes.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
              <span className="mt-2 block text-xs leading-5 text-muted-foreground">{activeMode.description}</span>
            </label>

            <div className="sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              <div className="grid min-w-0 grid-cols-2 gap-3">
                <NumberField id="ratio-a" label="A — первый член" field="a" value={inputs.a} placeholder="2" calculation={calculation} onChange={updateInput('a')} />
                <NumberField id="ratio-b" label="B — второй член" field="b" value={inputs.b} placeholder="3" calculation={calculation} onChange={updateInput('b')} />
                {mode === 'solve' && (
                  <NumberField id="ratio-c" label="C — известное значение" field="c" value={inputs.c} placeholder="8" calculation={calculation} onChange={updateInput('c')} />
                )}
                {mode === 'scale' && (
                  <NumberField id="ratio-multiplier" label="Множитель" field="multiplier" value={inputs.multiplier} placeholder="2" calculation={calculation} onChange={updateInput('multiplier')} />
                )}
              </div>

              {mode === 'solve' && (
                <div className="mt-4 rounded-md border bg-card px-3 py-2 text-center font-mono text-sm tabular-nums text-muted-foreground">
                  A : B = C : X
                </div>
              )}

              {calculation.error && (
                <div id="ratio-input-error" role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorText(calculation.error)}
                </div>
              )}
            </div>

          </div>

          <ResultPanel calculation={calculation}>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {calculation.result && <Button variant="outline" onClick={copyResult}>Скопировать результат</Button>}
              <Button variant="ghost" onClick={reset}>Сбросить</Button>
              <span role="status" className="text-sm text-primary">{copyStatus === 'copied' ? 'Результат скопирован.' : ''}</span>
              <span role="alert" className="text-sm text-destructive">{copyStatus === 'error' ? 'Не удалось скопировать результат.' : ''}</span>
            </div>
          </ResultPanel>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Точное сокращение" text="Целые и десятичные отношения сокращаются через точную целочисленную арифметику." />
        <InfoCard title="Понятная точность" text={`Длинные результаты округляются до ${RESULT_DECIMAL_PLACES} знаков с явной пометкой.`} />
        <InfoCard title="Без сервера" text="Введённые значения остаются в вашем браузере." />
      </section>
    </div>
  )
}

function NumberField({
  id,
  label,
  field,
  value,
  placeholder,
  calculation,
  onChange,
}: {
  id: string
  label: string
  field: RatioFieldKey
  value: string
  placeholder: string
  calculation: RatioCalculationState
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}) {
  const isInvalid = calculation.invalidFields.includes(field) || (value.trim() !== '' && parseDecimal(value) === null)

  return (
    <label htmlFor={id} className="block min-w-0">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      <input
        id={id}
        inputMode="decimal"
        maxLength={MAX_RATIO_INPUT_LENGTH}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? 'ratio-input-error' : undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-md border bg-background px-3 font-mono text-base tabular-nums outline-none transition-colors',
          'focus:border-primary focus:ring-2 focus:ring-ring/20',
          isInvalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
        )}
      />
    </label>
  )
}

function ResultPanel({ calculation, children }: { calculation: RatioCalculationState; children: ReactNode }) {
  return (
    <div aria-live="polite" aria-atomic="true" className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[350px]">
      <div className="text-sm font-medium text-muted-foreground">Результат</div>
      {calculation.result ? (
        <div className="mt-2 min-w-0 sm:mt-4">
          <div className="rounded-md border border-primary/35 bg-card p-5 shadow-sm">
            <div className="break-words font-mono text-3xl font-semibold tabular-nums sm:text-4xl">{calculation.result.display}</div>
            {calculation.result.rounded && (
              <p className="mt-3 text-xs leading-5 text-muted-foreground">Результат округлён до {RESULT_DECIMAL_PLACES} знаков после запятой.</p>
            )}
          </div>
          <div className="mt-5 rounded-md border bg-card p-4">
            <div className="text-xs font-medium uppercase text-muted-foreground">Формула</div>
            <div className="mt-2 break-words font-mono text-sm">{calculation.result.formula}</div>
            <p className="mt-3 break-words text-sm leading-6 text-muted-foreground">{calculation.result.detail}</p>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-md border bg-card p-4 shadow-sm">
          <div className="font-semibold">{calculation.status === 'error' ? 'Проверьте значения' : 'Введите значения'}</div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {calculation.status === 'error'
              ? 'После исправления полей результат обновится автоматически.'
              : 'Заполните обязательные поля — результат появится без отдельной команды.'}
          </p>
        </div>
      )}
      {children}
    </div>
  )
}

function errorText(error: RatioCalculationError): string {
  if (error === 'number_too_long') return `Введите число длиной не более ${MAX_RATIO_INPUT_LENGTH} символов.`
  if (error === 'division_by_zero') return 'В пропорции A не может быть равен нулю: деление на ноль невозможно.'
  if (error === 'undefined_ratio') return 'Отношение 0:0 не определено. Измените хотя бы один член отношения.'
  if (error === 'zero_multiplier') return 'Нулевой множитель превратит отношение в неопределённое 0:0. Введите ненулевое число.'

  return 'Введите обычное число с точкой или запятой, без экспоненты и лишних символов.'
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="pt-6">
        <div className="text-sm font-semibold">{title}</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}
